import { Button, Flex, Table } from 'antd'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { useTreatmentServiceGetPatientBirthListEndpoint } from 'src/api/queries'
import { BirthType, Permissions, PregnancyPeriodType } from 'src/api/requests'
import { Select } from 'src/components'
import {
  PersonPregnancyCreateBirthModal,
  useBirthColumns
} from 'src/features/persons/components'
import { useDefaultSortingItem, useTablePagination, useUser } from 'src/hooks'
import { useLocales } from 'src/locales'
import { resetPagination } from 'src/utils'

export const PersonPregnancyBirths = () => {
  const { t } = useLocales()
  const { personId, id } = useParams()

  const { hasPermission } = useUser()
  const [isOpen, setIsOpen] = useState(false)
  const { userData } = useUser()
  const pagination = useTablePagination()

  const { columns, birthId, onCancel } = useBirthColumns()
  const [pregnancyType, setPregnancyType] = useState('')
  const [birthType, setBirthType] = useState('')

  const { defaultSortingOption } = useDefaultSortingItem()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    personIds: [String(personId)],
    pregnancyMonitoringIds: [String(id)],
    institutionIds: [String(userData?.currentLegalEntityId)],
    ...(pregnancyType && {
      pregnancyPeriodType: [pregnancyType] as PregnancyPeriodType[]
    }),
    ...(birthType && { birthType: [birthType] as BirthType[] })
  }

  const { data, isLoading } =
    useTreatmentServiceGetPatientBirthListEndpoint(queryParams)

  const onBirthTypeSelect = (val: string) => {
    setBirthType(val)
    resetPagination(pagination)
  }

  const onPregnancySelect = (val: string) => {
    setPregnancyType(val)
    resetPagination(pagination)
  }

  const onToggle = () => {
    setIsOpen(false)
    onCancel()
  }

  const pregnancyTypeOptions = [
    defaultSortingOption,
    ...Object.values(PregnancyPeriodType).map((item) => ({
      value: item,
      label: t(`pregnancy.period_type.${item}`)
    }))
  ]

  const birthTypeOptions = [
    defaultSortingOption,
    ...Object.entries(BirthType).map(([_, value]) => ({
      value,
      label: t(`pregnancy.birth_type.${value}`)
    }))
  ]

  return (
    <Flex vertical gap={16}>
      <Flex gap={8} align="end" justify="end">
        <Select
          onSelect={onPregnancySelect}
          options={pregnancyTypeOptions}
          placeholder={t('pregnancy.pregnancy_type')}
        />

        <Select
          onSelect={onBirthTypeSelect}
          options={birthTypeOptions}
          placeholder={t('pregnancy.birth_type_status')}
        />

        {hasPermission(
          Permissions.TREATMENT_PREGNANCY_MONITORING_BIRTH_CREATE_BIRTH
        ) && (
          <Button type="primary" onClick={() => setIsOpen(true)}>
            {t('pregnancy.add_new_birth')}
          </Button>
        )}
      </Flex>

      <Table
        rowKey="id"
        scroll={{ x: 'max-content' }}
        loading={isLoading}
        columns={columns}
        dataSource={data?.data}
        pagination={{ ...pagination, total: data?.totalCount }}
      />

      {(isOpen || birthId) && (
        <PersonPregnancyCreateBirthModal id={birthId} onCancel={onToggle} />
      )}
    </Flex>
  )
}
